import axios from "./axios"
import { handleResponse } from "./util"

interface Ad {
  id: number,
  name: string,
  retailers: number[],
  suggested_text: string,
  credits: string,
  send_once: boolean,
  send_at_end: string,
  send_at_start: string,
  tracking_hours: string,
  status: number,
  selected_merchant_segment_ids: number[],
  category_list: string[]
  ads_merchants_attributes: AdMerchantAttribute[]
}

interface AdMerchantAttribute {
  id: number,
  selected_merchant_segment_ids?: number[],
  category_list?: string
}

interface AdAdditionalParams {
  selectAll: boolean,
  query: string
}

interface Categories {
  ads_merchant_id: number,
  context: string,
  search?: string,
  page: number
}

interface SegmentCountParams {
  advertiserId: number,
  adMerchantId: number,
  itemId: number
}

interface TagCountParams {
  advertiserId: number,
  context: string,
  ads_merchant_id: number,
  itemId: string
}

type AdScope = "all" | "sent" | "unsent"
interface Settings {
  brand_id: number,
  end_time: string,
  page: number,
  scope: AdScope,
  start_time: string,
}
interface AdRetailerParams {
  brand_id: number,
  brand_campaign_id: number
}

export const adAPI = {
  /**
   * POST request to create an ad
   * @param {number} advertiserId - current advertiser ID
   * @param {object} - extra params for new ad
   * @returns {object} new ad details
   */
  createAd(advertiserId: number, extraData: object) {
    const params = extraData || {}
    return axios.post(`/brands/${advertiserId}/brand_campaigns`, params).then(handleResponse)
  },

  /**
   * GET request to retrieve ad with passed in ID
   * @param {number} advertiserId - current advertiser ID
   * @param {number} adId - ad ID
   * @returns {object} retrieved ad details
   */
  getAd(advertiserId: number, adId: number) {
    return axios.get(`/brands/${advertiserId}/brand_campaigns/${adId}`).then(handleResponse)
  },

  /**
   * GET request to retrieve all ads that satisfied criteria in params
   * @param {Settings} params - contain criteria: advertiser ID, start time, end time, scope, current page
   */
  getAds(params: Settings) {
    return axios.get('/ads', { params: params }).then(handleResponse)
  },

  /**
   * PATCH request to update ad with attributes
   * @param {number} advertiserId - current advertiser ID
   * @param {object} data - update attribute
   * @param {object} additionalParams - params for retailer filters on step 1
   */
  updateAd(advertiserId: number, data: Partial<Ad>, additionalParams: AdAdditionalParams) {
    const params = additionalParams?.selectAll ? `?select_all=true${additionalParams.query}` : ''
    return axios.patch(`/brands/${advertiserId}/brand_campaigns/${data.id}${params}`, data)
      .then(handleResponse)
  },

  // ----- CUSTOMER FILTERS ON ADS ----- //
  /**
   * API request to get all available filters for the selected merchant (locations, categories, transaction based)
   * @param {number} advertiserId - current advertiser ID
   * @param {number} adMerchantId - ID of ad merchant record created for the previously selected retailer on step 1
   */
  getGroups(advertiserId: number, adMerchantId: number) {
    return axios.get(`/brands/${advertiserId}/groups`, {
      params: {
        ads_merchant_id: adMerchantId
      }
    }).then(handleResponse)
  },

  /**
   * GET request to get saved selected filters
   * @param {number} advertiserId - current advertiser ID
   * @param adMerchantId - ID of ad merchant record created for the previously selected retailer on step 1
   */
  getFilters(advertiserId: number, adMerchantId: number) {
    return axios.get(`/brands/${advertiserId}/groups/${adMerchantId}`)
      .then(handleResponse)
  },

  /**
   * API request to get all available segments (items) under locations/transaction based's subgroups
   * @param {number} advertiserId - current advertiser ID
   * @param {number} adMerchantId - ID of ad merchant record created for the previously selected retailer on step 1
   * @param {number} segmentId - selected group ID
   * @param {number} currentPage - current page of the segments list
   * @param {string} searchTerm - search term user puts into the search bar in dropdown
   */
  getSegments(advertiserId: number, adMerchantId: number, segmentId: number, currentPage: number, searchTerm: string) {
    return axios.get(`/brands/${advertiserId}/groups/merchant_segments`, {
      params: {
        ads_merchant_id: adMerchantId,
        segment_id: segmentId,
        page: currentPage,
        ...!!searchTerm && { search: searchTerm }
      }
    }).then(handleResponse)
  },

  /**
   * API request to get sms/email count for each segment item
   * @param {SegmentCountParams} params - contains needed data:
  *  advertiser ID (advertiserId),
  *  ID of ad merchant record created for the previously selected retailer on step 1 (adMerchantId),
  *  segment item ID (itemId)
   */
  getSegmentCount(params: SegmentCountParams) {
    return axios.get(`/brands/${params.advertiserId}/groups/segment_count`, {
      params: {
        ads_merchant_id: params.adMerchantId,
        segment_id: params.itemId
      }
    }).then(handleResponse)
  },

  /**
   * API request to get all available tags (items) under categories
   * @param {number} advertiserId - current advertiser ID
   * @param {Categories} params - contains needed data:
   * ID of ad merchant record created for the previously selected retailer on step 1 (adMerchantId),
   * context of current view tag (context),
   * search term (if user types in any) - optional (search),
   * current page (page)
   */
  getTags(advertiserId: number, params: Categories) {
    return axios.get(`/brands/${advertiserId}/groups/merchant_taggings`, {
      params: params
    }).then(handleResponse)
  },

  /**
   * API request to get sms/email count for each tagging item
   * @param {TagCountParams} params - contains needed data:
   * advertiser ID (advertiserId),
   * ID of ad merchant record created for the previously selected retailer on step 1 (adMerchantId),
   * parent group name (context),
   * name of the tag item (itemId)
   */
  getTagCount(params: TagCountParams) {
    return axios.get(`/brands/${params.advertiserId}/groups/tag_count`, {
      params: {
        context: params.context,
        ads_merchant_id: params.ads_merchant_id,
        tag: params.itemId
      }
    }).then(handleResponse)
  },

  /**
   * API request to get max recipients for the selected filters
   * @param {number} advertiserId - current advertiser ID
   * @param {number} adMerchantId - ID of ad merchant record created for the previously selected retailer on step 1
   */
  getMaxRecipients(advertiserId: number, adMerchantId: number) {
    return axios.get(`/brands/${advertiserId}/groups/total_textable_members`, {
      params: {
        ads_merchant_id: adMerchantId
      }
    })
      .then(handleResponse)
  },

  //------------------------ PERFORMANCE APIS ------------------------//
  getAdRetailers(params: AdRetailerParams) {
    return axios.get(`/performance/ad_retailers`, { params }).then(handleResponse)
  }
}